import React, { useState, useEffect } from 'react';
import { pickingTasksAPI, PickingTask, QCCheckType } from '../../services/api';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  ArrowLeftIcon,
  CubeIcon,
  DocumentTextIcon,
  ScaleIcon,
  TagIcon,
  ShieldCheckIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';

interface QCCheckpointProps {
  taskId: number;
  onBack: () => void;
  onComplete: () => void;
}

const qcCheckConfig: Record<QCCheckType, { label: string; description: string; icon: React.ReactNode }> = {
  CORRECT_PRODUCTS: { label: 'Correct Products', description: 'All products match the order specification', icon: <CubeIcon className="w-5 h-5" /> },
  SEAL_INTEGRITY: { label: 'Seal Integrity', description: 'All seals are intact and undamaged', icon: <ShieldCheckIcon className="w-5 h-5" /> },
  NO_LEAKS: { label: 'No Leaks', description: 'No visible leaks or damage to containers', icon: <ExclamationCircleIcon className="w-5 h-5" /> },
  LABEL_ACCURACY: { label: 'Label Accuracy', description: 'All labels are correct and legible', icon: <TagIcon className="w-5 h-5" /> },
  WEIGHT_VERIFIED: { label: 'Weight Verified', description: 'Package weight matches expected weight', icon: <ScaleIcon className="w-5 h-5" /> },
  PACKAGING_QUALITY: { label: 'Packaging Quality', description: 'Packaging is secure and appropriate', icon: <CubeIcon className="w-5 h-5" /> },
  DOCUMENTATION_COMPLETE: { label: 'Documentation Complete', description: 'All required documents are included', icon: <DocumentTextIcon className="w-5 h-5" /> },
};

const requiredChecks: QCCheckType[] = ['CORRECT_PRODUCTS', 'SEAL_INTEGRITY', 'NO_LEAKS', 'LABEL_ACCURACY'];

export const QCCheckpoint: React.FC<QCCheckpointProps> = ({ taskId, onBack, onComplete }) => {
  const [task, setTask] = useState<PickingTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checks, setChecks] = useState<Record<QCCheckType, { passed: boolean | null; notes: string }>>({} as any);
  const [overallNotes, setOverallNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [packingData, setPackingData] = useState({ totalWeight: '', totalPackages: '', packagingNotes: '' });

  useEffect(() => {
    loadTask();
  }, [taskId]);

  useEffect(() => {
    // Initialize checks
    const initialChecks: Record<QCCheckType, { passed: boolean | null; notes: string }> = {} as any;
    Object.keys(qcCheckConfig).forEach((key) => {
      initialChecks[key as QCCheckType] = { passed: null, notes: '' };
    });
    setChecks(initialChecks);
  }, []);

  const loadTask = async () => {
    try {
      setLoading(true);
      const data = await pickingTasksAPI.getById(taskId);
      setTask(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  const handleStartQC = async () => {
    if (!task) return;
    try {
      const updated = await pickingTasksAPI.startQC(task.id);
      setTask(updated);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCheckChange = (checkType: QCCheckType, passed: boolean) => {
    setChecks(prev => ({ ...prev, [checkType]: { ...prev[checkType], passed } }));
  };

  const handleNotesChange = (checkType: QCCheckType, notes: string) => {
    setChecks(prev => ({ ...prev, [checkType]: { ...prev[checkType], notes } }));
  };

  const allRequiredChecksComplete = requiredChecks.every(check => checks[check]?.passed !== null);
  const allChecksPassed = requiredChecks.every(check => checks[check]?.passed === true);

  const handleSubmitQC = async () => {
    if (!task) return;
    try {
      setSubmitting(true);
      const checkData = Object.entries(checks)
        .filter(([_, value]) => value.passed !== null)
        .map(([checkType, value]) => ({
          checkType: checkType as QCCheckType,
          passed: value.passed!,
          notes: value.notes || undefined,
        }));
      await pickingTasksAPI.submitQCChecklist({ pickingTaskId: task.id, checks: checkData, overallNotes: overallNotes || undefined });
      await loadTask();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompletePacking = async () => {
    if (!task) return;
    try {
      setSubmitting(true);
      await pickingTasksAPI.completePacking({
        pickingTaskId: task.id,
        totalWeight: packingData.totalWeight ? parseFloat(packingData.totalWeight) : undefined,
        totalPackages: packingData.totalPackages ? parseInt(packingData.totalPackages) : undefined,
        packagingNotes: packingData.packagingNotes || undefined,
      });
      onComplete();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  }

  if (!task) {
    return <div className="text-center py-12 text-red-600"><ExclamationCircleIcon className="w-12 h-12 mx-auto mb-4" /><p>Task not found</p></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeftIcon className="w-5 h-5" /> Back
        </button>
        <div className="text-right">
          <div className="text-sm text-gray-500">Task #{task.id}</div>
          <div className="font-semibold">{task.deal?.orderNumber || `Deal #${task.dealId}`}</div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <ExclamationCircleIcon className="w-5 h-5 inline mr-2" />{error}
          <button onClick={() => setError(null)} className="ml-4 text-sm underline">Dismiss</button>
        </div>
      )}

      {/* Start QC Button */}
      {task.status === 'PICKED' && (
        <div className="text-center py-8 bg-white rounded-lg shadow-sm border">
          <ClipboardDocumentCheckIcon className="w-16 h-16 mx-auto mb-4 text-blue-600" />
          <h2 className="text-xl font-semibold mb-2">Ready for Quality Control</h2>
          <p className="text-gray-600 mb-6">All items have been picked. Proceed to QC verification.</p>
          <button onClick={handleStartQC} className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Start QC Inspection
          </button>
        </div>
      )}

      {/* QC Checklist */}
      {task.status === 'QC_PENDING' && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-4 py-3 border-b bg-yellow-50">
            <h2 className="font-semibold flex items-center gap-2">
              <ClipboardDocumentCheckIcon className="w-5 h-5 text-yellow-600" />
              Mandatory QC Checklist
            </h2>
            <p className="text-sm text-gray-600">Complete all required checks before packing</p>
          </div>
          <div className="divide-y">
            {requiredChecks.map((checkType) => {
              const config = qcCheckConfig[checkType];
              const check = checks[checkType];
              return (
                <div key={checkType} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg">{config.icon}</div>
                      <div>
                        <div className="font-medium">{config.label}</div>
                        <div className="text-sm text-gray-500">{config.description}</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleCheckChange(checkType, true)}
                        className={`p-2 rounded-lg border-2 ${check?.passed === true ? 'border-green-500 bg-green-50 text-green-600' : 'border-gray-200 text-gray-400 hover:border-green-300'}`}>
                        <CheckCircleIcon className="w-6 h-6" />
                      </button>
                      <button onClick={() => handleCheckChange(checkType, false)}
                        className={`p-2 rounded-lg border-2 ${check?.passed === false ? 'border-red-500 bg-red-50 text-red-600' : 'border-gray-200 text-gray-400 hover:border-red-300'}`}>
                        <XCircleIcon className="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                  {check?.passed === false && (
                    <div className="mt-3 ml-11">
                      <input type="text" value={check.notes} onChange={(e) => handleNotesChange(checkType, e.target.value)}
                        placeholder="Add notes about the issue..." className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="p-4 border-t bg-gray-50">
            <label className="block text-sm font-medium text-gray-700 mb-2">Overall QC Notes</label>
            <textarea value={overallNotes} onChange={(e) => setOverallNotes(e.target.value)}
              placeholder="Add any additional notes..." className="w-full px-3 py-2 border rounded-lg text-sm" rows={2} />
          </div>
          <div className="p-4 border-t">
            <button onClick={handleSubmitQC} disabled={!allRequiredChecksComplete || submitting}
              className={`w-full py-3 rounded-lg font-medium ${allChecksPassed ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'} disabled:opacity-50`}>
              {submitting ? 'Submitting...' : allChecksPassed ? 'Submit QC - All Passed' : 'Submit QC - Issues Found'}
            </button>
          </div>
        </div>
      )}

      {/* QC Passed - Packing */}
      {task.status === 'QC_PASSED' && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-4 py-3 border-b bg-green-50">
            <h2 className="font-semibold flex items-center gap-2 text-green-700">
              <CheckCircleIcon className="w-5 h-5" /> QC Passed - Ready for Packing
            </h2>
          </div>
          <div className="p-4 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Weight (kg)</label>
                <input type="number" step="0.001" value={packingData.totalWeight} onChange={(e) => setPackingData(p => ({ ...p, totalWeight: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg" placeholder="0.000" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Packages</label>
                <input type="number" value={packingData.totalPackages} onChange={(e) => setPackingData(p => ({ ...p, totalPackages: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg" placeholder="1" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Packaging Notes</label>
              <textarea value={packingData.packagingNotes} onChange={(e) => setPackingData(p => ({ ...p, packagingNotes: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg" rows={2} placeholder="Box type, special handling, etc." />
            </div>
            <button onClick={handleCompletePacking} disabled={submitting}
              className="w-full py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2">
              <CubeIcon className="w-5 h-5" /> {submitting ? 'Completing...' : 'Complete Packing'}
            </button>
          </div>
        </div>
      )}

      {/* QC Failed */}
      {task.status === 'QC_FAILED' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <XCircleIcon className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-semibold text-red-700 mb-2">QC Failed</h2>
          <p className="text-red-600 mb-4">{task.qcNotes || 'Quality control issues were found.'}</p>
          <button onClick={onBack} className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
            Return to Task List
          </button>
        </div>
      )}

      {/* Packed */}
      {task.status === 'PACKED' && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
          <CubeIcon className="w-16 h-16 mx-auto mb-4 text-emerald-500" />
          <h2 className="text-xl font-semibold text-emerald-700 mb-2">Packing Complete!</h2>
          <p className="text-emerald-600">This order is ready for shipment.</p>
        </div>
      )}
    </div>
  );
};

export default QCCheckpoint;

